import React from "react";
import wave from "../resource/wave_final.png";
import socialBg from "../resource/socialBg1.png";
import event1 from "../resource/event1.jpg";
import event2 from "../resource/event2.jpg";
import event3 from "../resource/event3.jpg";
import event4 from "../resource/event4.jpg";
import event5 from "../resource/event5.jpg";
import event6 from "../resource/event6.jpg";
import { FaCalendarAlt, FaMapMarkerAlt, FaUsers, FaLightbulb, FaChartLine, FaHandshake } from "react-icons/fa";
import { Sparkles } from 'lucide-react';

export default function Event() {
  const upcomingEvents = [
    {
      name: "Auto Expo 2025",
      date: "March 15-20, 2026",
      location: "Delhi",
      description: "Asia's largest automotive show"
    },
    {
      name: "EV Summit India",
      date: "April 10-12, 2026",
      location: "Bangalore",
      description: "Electric Vehicle innovation summit"
    },
    {
      name: "Green Mobility Conference",
      date: "May 5-7, 2026",
      location: "Mumbai",
      description: "Sustainable transportation solutions"
    },
    {
      name: "Tech Startup Fest",
      date: "June 20-22, 2026",
      location: "Hyderabad",
      description: "Emerging tech and innovation showcase"
    }
  ];

  const testimonials = [
    {
      quote: "The hybrid conversion kit demonstration was mind-blowing! Truly revolutionary technology.",
      author: "Rajesh Kumar",
      company: "Auto Magazine"
    },
    {
      quote: "One of the most innovative startups at Startup Mahakumbh 2025. The future of mobility!",
      author: "Priya Sharma",
      company: "Tech Times"
    },
    {
      quote: "Their hybrid technology bridge between petrol and electric is exactly what India needs.",
      author: "Amit Patel",
      company: "Green Mobility Council"
    }
  ];

  return (
    <div className="relative w-full overflow-hidden bg-gradient-to-b from-white to-green-50">
      {/* Animated Background Wave */}
      <img
        src={wave}
        className="w-full h-full absolute top-0 left-0 opacity-30 object-cover z-0 animate-float-slow"
        alt="Wave Background"
      />

      {/* Main content container */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-12 md:py-20 space-y-16">

        {/* Hero Section */}
        <div className="max-w-6xl mx-auto text-center space-y-8 animate-fade-in">
          {/* Premium AI Badge */}
          <div className="z-10 flex justify-center group transition-all duration-500 hover:scale-110 active:scale-95">
            <span className="relative overflow-hidden bg-black/90 backdrop-blur-xl px-5 py-2.5 rounded-full shadow-[0_0_20px_rgba(34,197,94,0.4)] flex items-center gap-3 border border-green-500/50">
              <span className="absolute inset-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -rotate-45 shimmer-sweep"></span>
              <Sparkles className="w-4 h-4 text-green-400 animate-pulse" />
              <span className="text-white text-[10px] sm:text-xs font-black tracking-[0.2em] uppercase">
                SMART <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">EVENT INTELLIGENCE</span>
              </span>
            </span>
          </div>

          <div className="inline-flex items-center justify-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium animate-bounce-subtle">
            <FaCalendarAlt className="animate-spin-slow" /> Our Event Journey
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-green-900 leading-tight animate-slide-up">
            Where <span className="text-green-600 animate-pulse-slow">Innovation Meets</span> The Road
          </h1>

          <p className="text-gray-700 text-lg sm:text-xl max-w-4xl mx-auto leading-relaxed animate-slide-up-delay">
            From <span className="font-semibold text-green-700 animate-pulse-slow">Startup Mahakumbh</span> to national
            expos, we're showcasing how our hybrid technology is revolutionizing Indian mobility.
            Experience the future of transportation firsthand!
          </p>
        </div>

        {/* Highlight Box */}
        <div className="max-w-6xl mx-auto bg-gradient-to-r from-green-600 to-green-700 text-white rounded-2xl p-6 md:p-8 shadow-2xl animate-pulse-glow">
          <div className="flex items-center gap-3 mb-4 animate-shake-slow">
            <FaLightbulb className="text-2xl text-yellow-300 animate-spin-slow" />
            <h2 className="text-2xl font-bold">🌟 Startup Mahakumbh 2025 - Our Biggest Showcase!</h2>
          </div>
          <p className="text-lg leading-relaxed">
            Our booth was one of the <span className="font-bold text-yellow-300 animate-ping-subtle">most visited</span> at India's
            largest startup congregation. We demonstrated <span className="text-yellow-200 font-bold underline decoration-yellow-400">AI-optimized live conversions</span>, engaged with 5000+ visitors.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <span className="bg-green-800 px-3 py-1 rounded-full text-sm animate-badge-pop">5000+ Visitors</span>
            <span className="bg-green-800 px-3 py-1 rounded-full text-sm animate-badge-pop animation-delay-100">Live Demos</span>
            <span className="bg-green-800 px-3 py-1 rounded-full text-sm animate-badge-pop animation-delay-200">Media Coverage</span>
          </div>
        </div>

        {/* Mahakumbh Image Grid Section */}
        <div className="max-w-6xl mx-auto animate-fade-in-up">
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-green-200">
            <div className="flex items-center justify-center gap-3 mb-6">
              <FaUsers className="text-3xl text-green-600 animate-bounce" />
              <h2 className="text-2xl md:text-3xl font-bold text-green-900 text-center">
                Glimpse from Startup Mahakumbh 2025
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {[event1, event2, event3, event4, event5, event6].map((src, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-xl shadow-lg animate-float"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <img
                    src={src}
                    alt={`Event ${index + 1}`}
                    className="w-full h-48 md:h-64 object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                  />

                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-white to-green-50 rounded-2xl shadow-xl p-6 md:p-8 border border-green-200 animate-slide-in-right">
            <div className="flex items-center justify-between mb-8">
              <div className="animate-fade-in-left">
                <h3 className="text-2xl md:text-3xl font-bold text-green-900">Upcoming Events</h3>
                <p className="text-gray-600">Meet us at these exciting venues!</p>
              </div>
              <FaCalendarAlt className="text-4xl text-green-600 animate-spin-once" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {upcomingEvents.map((event, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-5 shadow-lg border border-green-100 hover:border-green-400 transition-all duration-300 flex flex-col h-full animate-card-pop"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="flex items-center gap-2 text-green-700 mb-3">
                    <FaMapMarkerAlt className="animate-pulse" />
                    <span className="font-medium">{event.location}</span>
                  </div>
                  <h4 className="text-xl font-bold text-gray-800 mb-2">{event.name}</h4>
                  <p className="text-gray-600 text-sm mb-4 flex-grow">{event.description}</p>

                  <div className="mt-auto">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                      <FaCalendarAlt className="animate-wiggle" />
                      <span>{event.date}</span>
                    </div>
                    <button className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 active:scale-95 animate-button-pulse">
                      Book Meeting
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="max-w-6xl mx-auto animate-fade-in-up">
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-green-200">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full mb-4 animate-bounce-subtle">
                <FaHandshake className="animate-spin-slow" /> What People Say
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-green-900">Event Testimonials</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-green-50 to-white rounded-xl p-6 shadow-lg border border-green-200 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 animate-testimonial-slide"
                  style={{ animationDelay: `${index * 200}ms` }}
                >
                  <div className="text-green-500 text-4xl mb-4 animate-quote-appear">"</div>
                  <p className="text-gray-700 italic mb-6 animate-fade-in">{testimonial.quote}</p>
                  <div className="border-t pt-4 animate-border-grow">
                    <p className="font-bold text-green-800">{testimonial.author}</p>
                    <p className="text-sm text-gray-600">{testimonial.company}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stay Connected Section */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-green-900 to-green-800 rounded-2xl p-6 md:p-8 text-white shadow-2xl animate-glow">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold mb-4 animate-text-glow">Stay Connected With Our Journey</h3>
              <p className="text-green-100 max-w-3xl mx-auto animate-fade-in-delay">
                Follow us for live event updates, behind-the-scenes content, and exclusive announcements
              </p>
            </div>

            <div className="relative overflow-hidden rounded-xl group">
              <img
                src={socialBg}
                alt="Social Media"
                className="w-full h-auto object-cover rounded-xl transform transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-green-900/80 via-green-900/40 to-transparent flex flex-col justify-end p-6 transition-all duration-500 group-hover:via-green-900/60">
                <p className="text-xl font-bold mb-2 transform transition-all duration-500 group-hover:translate-y-0 translate-y-2">Follow @KGVHybrid</p>
                <p className="text-green-100 transform transition-all duration-500 group-hover:translate-y-0 translate-y-2 opacity-90 group-hover:opacity-100">
                  For real-time event updates and innovation stories
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto text-center animate-bounce-subtle">
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-2xl p-8 shadow-xl animate-cta-pulse">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Want KGV at Your Event?
            </h3>
            <p className="text-lg mb-6 max-w-2xl mx-auto">
              Invite us to showcase our hybrid technology at your expo, conference, or corporate event
            </p>
            <button className="bg-white text-green-700 hover:bg-green-50 px-8 py-3 rounded-lg font-bold text-lg shadow-lg transform transition-all duration-300 hover:scale-110 hover:shadow-2xl active:scale-105 animate-button-glow">
              Invite to Event
            </button>
          </div>
        </div>

      </div>

      {/* Add CSS Animations */}
      <style jsx global>{`
        @keyframes shimmerSweep {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
          100% { transform: translateX(100%); }
        }
        .shimmer-sweep {
          animation: shimmerSweep 3s infinite linear;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          33% { transform: translateY(-10px) translateX(5px); }
          66% { transform: translateY(5px) translateX(-5px); }
        }
        
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slide-up {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        
        @keyframes shake-slow {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-2px); }
          75% { transform: translateX(2px); }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes ping-subtle {
          75%, 100% { transform: scale(1.1); opacity: 0; }
        }
        
        @keyframes badge-pop {
          0% { transform: scale(0.8); opacity: 0; }
          70% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes slide-in-right {
          from { transform: translateX(50px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        
        @keyframes fade-in-left {
          from { transform: translateX(-30px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        
        @keyframes spin-once {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-5deg); }
          75% { transform: rotate(5deg); }
        }
        
        @keyframes button-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4); }
          50% { box-shadow: 0 0 0 10px rgba(34, 197, 94, 0); }
        }
        
        @keyframes quote-appear {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        
        @keyframes border-grow {
          from { width: 0; opacity: 0; }
          to { width: 100%; opacity: 1; }
        }
        
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(34, 197, 94, 0.5); }
          50% { box-shadow: 0 0 40px rgba(34, 197, 94, 0.8); }
        }
        
        @keyframes text-glow {
          0%, 100% { text-shadow: 0 0 10px rgba(255, 255, 255, 0.5); }
          50% { text-shadow: 0 0 20px rgba(255, 255, 255, 0.8); }
        }
        
        @keyframes cta-pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.02); }
          100% { transform: scale(1); }
        }
        
        @keyframes button-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(255, 255, 255, 0.5); }
          50% { box-shadow: 0 0 40px rgba(255, 255, 255, 0.8); }
        }
        
        @keyframes card-pop {
          0% { transform: scale(0.9) translateY(20px); opacity: 0; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        
        @keyframes testimonial-slide {
          from { transform: translateX(-30px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        
        @keyframes fade-in-up {
          from { transform: translateY(40px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        @keyframes pulse-glow {
          0%, 100% { 
            box-shadow: 0 0 20px rgba(34, 197, 94, 0.5);
            opacity: 1;
          }
          50% { 
            box-shadow: 0 0 40px rgba(34, 197, 94, 0.8);
            opacity: 0.95;
          }
        }

        /* Animation Classes */
        .animate-float { 
          animation: float 6s ease-in-out infinite; 
        }
        
        .animate-float-slow { 
          animation: float-slow 20s ease-in-out infinite; 
        }
        
        .animate-fade-in { 
          animation: fade-in 1s ease-out forwards; 
        }
        
        .animate-fade-in-up { 
          animation: fade-in-up 0.8s ease-out forwards; 
        }
        
        .animate-slide-up { 
          animation: slide-up 0.8s ease-out forwards; 
        }
        
        .animate-slide-up-delay { 
          animation: slide-up 0.8s ease-out 0.3s forwards; 
        }
        
        .animate-pulse-slow { 
          animation: pulse-slow 3s ease-in-out infinite; 
        }
        
        .animate-bounce-subtle { 
          animation: bounce-subtle 2s ease-in-out infinite; 
        }
        
        .animate-pulse-glow { 
          animation: pulse-glow 4s ease-in-out infinite; 
        }
        
        .animate-shake-slow { 
          animation: shake-slow 3s ease-in-out infinite; 
        }
        
        .animate-spin-slow { 
          animation: spin-slow 20s linear infinite; 
        }
        
        .animate-ping-subtle { 
          animation: ping-subtle 2s cubic-bezier(0, 0, 0.2, 1) infinite; 
        }
        
        .animate-badge-pop { 
          animation: badge-pop 0.5s ease-out forwards; 
        }
        
        .animate-slide-in-right { 
          animation: slide-in-right 0.8s ease-out forwards; 
        }
        
        .animate-fade-in-left { 
          animation: fade-in-left 0.8s ease-out forwards; 
        }
        
        .animate-spin-once { 
          animation: spin-once 0.5s ease-out; 
        }
        
        .animate-wiggle { 
          animation: wiggle 1s ease-in-out infinite; 
        }
        
        .animate-button-pulse { 
          animation: button-pulse 2s infinite; 
        }
        
        .animate-quote-appear { 
          animation: quote-appear 0.5s ease-out forwards; 
        }
        
        .animate-border-grow { 
          animation: border-grow 1s ease-out forwards; 
        }
        
        .animate-glow { 
          animation: glow 3s ease-in-out infinite; 
        }
        
        .animate-text-glow { 
          animation: text-glow 2s ease-in-out infinite; 
        }
        
        .animate-fade-in-delay { 
          animation: fade-in 1s ease-out 0.5s forwards; 
        }
        
        .animate-cta-pulse { 
          animation: cta-pulse 3s ease-in-out infinite; 
        }
        
        .animate-button-glow { 
          animation: button-glow 2s ease-in-out infinite; 
        }
        
        .animate-card-pop { 
          animation: card-pop 0.6s ease-out forwards; 
        }
        
        .animate-testimonial-slide { 
          animation: testimonial-slide 0.8s ease-out forwards; 
        }

        /* Animation Delay Classes */
        .animation-delay-100 { animation-delay: 100ms !important; }
        .animation-delay-200 { animation-delay: 200ms !important; }
        .animation-delay-300 { animation-delay: 300ms !important; }
        .animation-delay-400 { animation-delay: 400ms !important; }
        .animation-delay-500 { animation-delay: 500ms !important; }
        
        /* Ensure animations only play once on load for certain elements */
        .animate-fade-in,
        .animate-slide-up,
        .animate-slide-in-right,
        .animate-card-pop,
        .animate-testimonial-slide {
          animation-fill-mode: forwards;
        }
      `}</style>
    </div>
  );
};